import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'

import {
  INTEGRATIONS_WITH_DYNAMIC_SCHEMA,
  INTEGRATIONS_WITH_SEGMENTED_CONTACT_CHANGE,
  IntegrationServiceId,
  isContactCreateEnabledInConfig,
} from '../../../common/integrations'
import { useIntegrations } from '../../Integration/useIntegrations'

import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'

import { useCreateExternalUserMutation, useLazyGetAllExternalUsersQuery, useSaveExternalUserByIdMutation } from '../../../store/externalUser/externalUser'
import { useUploadFileMutation } from '../../../store/media/file'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { selectSelectedKeys } from '../../../store/meetingSummary/contactUpdate'
import { useCreateDocumentMutation, useMarkDocumentFileUploadedMutation, useProcessDocumentsMutation } from '../../../store/ai/documents'
import { type AIAgentAttachment, useExecuteAiAgentMutation } from '../../../store/ai/aiAgent'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  selectDocumentProspectingBaseFormValues,
  selectDocumentProspectingContact,
  selectDocumentProspectingCurrentUser,
  selectDocumentProspectingDocumentId,
  selectDocumentProspectingIntegrationContactExternalId,
  selectDocumentProspectingServiceId,
  setDocumentProspectingAttachment,
  setDocumentProspectingBaseFormValues,
  setDocumentProspectingBaseFormValuesLoading,
  setDocumentProspectingContact,
  setDocumentProspectingDetailsFormProps,
  setDocumentProspectingDetailsFormValues,
  setDocumentProspectingDetailsFormValuesLoading,
  setDocumentProspectingDocumentId,
  setDocumentProspectingIntegrationAsSuccess,
  setDocumentProspectingIntegrationContact,
  setDocumentProspectingIntegrationContactErrors,
  setDocumentProspectingSelectedFileName,
  setDocumentProspectingUploadIsLoading,
  setDocumentProspectingUploadedFileId,
} from '../../../store/processDocument/documentProspecting'
import {
  useCreateIntegrationContactMutation,
  useLazyGetIntegrationUsersQuery,
  useUpdateIntegrationUserMutation,
} from '../../../store/integrations/integrations'

import { ExternalUserServiceIntegration, User, UserOwnerType } from '../../../models/User'
import { AIAgentType } from '../../../models/AIAgent'
import { type DocumentCreate, DocumentFileType } from '../../../models/Document'

import {
  SegmentedDocumentProspectingDetailsFormSchemaType,
  SegmentedDocumentProspectingDetailsFormValuesType,
  DocumentProspectingBaseFormExecutionResultType,
  DocumentProspectingDetailsFormProps,
  ProspectingDetailsFormValues,
  ProspectingBaseFormValues,
} from '../../../models/DocumentProspecting'

import { filterDataByKeys, processContactUpdateAgentResult, removeEmptyFields } from '../../UpdateExternalContact/utils'
import { useAccount } from '../../../common/useAccount'
import { getEffectiveAccountIntegrationConfig } from '../../../models/Account'
import { DEFAULT_CONTACT_ROLE_ID } from '../../Contacts/AddContactModal'

import { type FileBrowserItem } from '../../../components/DocumentFileBrowser/types'

import { hashFile, inferContentType } from '../DocumentUpload/utils'

import { buildName, mapProspectingBaseFormExecutionResult } from './utils'

export const useDocumentProspectingEligibleIntegrations = () => {
  const currentUser = useAppSelector(selectDocumentProspectingCurrentUser)
  const { account } = useAccount()
  const { supportsDocumentContactUpdate } = useIntegrations()

  return useMemo(
    () =>
      currentUser?.integrations?.filter(
        ({ serviceId }) => supportsDocumentContactUpdate(serviceId) && isContactCreateEnabledInConfig(getEffectiveAccountIntegrationConfig(account, serviceId)),
      ) ?? [],
    [currentUser, account, supportsDocumentContactUpdate],
  )
}

export const useDocumentProspectingContacts = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const currentUserId = useAppSelector(selectSafeAuthenticatedUser).id
  const contact = useAppSelector(selectDocumentProspectingContact)

  const [triggerCreateIntegrationUser] = useCreateIntegrationContactMutation()
  const [triggerUpdateIntegrationUser] = useUpdateIntegrationUserMutation()
  const [triggerGetIntegrationUsers] = useLazyGetIntegrationUsersQuery()
  const [triggerCreateExternalUser] = useCreateExternalUserMutation()
  const [triggerUpdateExternalUser] = useSaveExternalUserByIdMutation()
  const [triggerGetAllExternalUsers] = useLazyGetAllExternalUsersQuery()

  const createContact = useCallback(
    async (baseFormValues: ProspectingBaseFormValues) => {
      try {
        const { firstName, lastName, email } = baseFormValues

        const name = buildName(firstName, lastName)

        const { data: createExternalUserResponse } = await triggerCreateExternalUser({
          accountId,
          userData: {
            firstName,
            lastName,
            email,
            name,
            owner: {
              ownerType: UserOwnerType.USER,
              ownerId: currentUserId,
            },
            roleIds: [DEFAULT_CONTACT_ROLE_ID],
            identity: { type: 'external', location: email },
            referenceUserId: null,
          },
        })
        if (!createExternalUserResponse) {
          return null
        }
        const { data: externalUser } = createExternalUserResponse
        dispatch(setDocumentProspectingContact(externalUser))
        return externalUser
      } catch (e) {
        console.error(e)
        return null
      }
    },
    [accountId, currentUserId, dispatch, triggerCreateExternalUser],
  )

  const updateContact = useCallback(
    async (contact: User, integration: ExternalUserServiceIntegration) => {
      try {
        const { id: userId, integrations } = contact
        const { data: updateExternalUserResponse } = await triggerUpdateExternalUser({
          accountId,
          userId,
          userData: {
            integrations: integrations ? [...integrations, integration] : [integration],
            owner: {
              ownerType: UserOwnerType.USER,
              ownerId: currentUserId,
            },
            referenceUserId: null,
          },
        })
        if (!updateExternalUserResponse) {
          return null
        }
        const { data: externalUser } = updateExternalUserResponse
        dispatch(setDocumentProspectingContact(externalUser))
        return externalUser
      } catch (e) {
        console.error(e)
        return null
      }
    },
    [accountId, currentUserId, dispatch, triggerUpdateExternalUser],
  )

  const createIntegrationContact = useCallback(
    async (serviceId: IntegrationServiceId, baseFormValues: ProspectingBaseFormValues, detailsFormValues?: ProspectingDetailsFormValues) => {
      try {
        const { firstName, lastName, email } = baseFormValues

        const contact = detailsFormValues?.data ?? {}
        const name = buildName(firstName, lastName)

        const { data: createIntegrationUserResponse } = await triggerCreateIntegrationUser({
          accountId,
          connector: serviceId,
          firstName,
          lastName,
          email,
          name,
          contact,
        })
        if (!createIntegrationUserResponse) {
          return null
        }
        const { data: integrationUser, errors } = createIntegrationUserResponse
        dispatch(setDocumentProspectingIntegrationContact(integrationUser))
        if (errors) {
          dispatch(setDocumentProspectingIntegrationContactErrors(errors))
        }
        return integrationUser
      } catch (e) {
        console.error(e)
        return null
      }
    },
    [accountId, dispatch, triggerCreateIntegrationUser],
  )

  const updateIntegrationContact = useCallback(
    async (serviceId: IntegrationServiceId, externalId: string, detailsFormValues: ProspectingDetailsFormValues) => {
      try {
        const relatedTo = { id: externalId }
        const { data: payload } = detailsFormValues
        const { data: updateIntegrationUserResponse } = await triggerUpdateIntegrationUser({
          accountId,
          connector: serviceId,
          relatedTo,
          payload,
        })
        if (!updateIntegrationUserResponse) {
          return null
        }
        const { data: integrationUser, errors } = updateIntegrationUserResponse
        dispatch(setDocumentProspectingIntegrationContact(integrationUser))
        if (errors) {
          dispatch(setDocumentProspectingIntegrationContactErrors(errors))
        }
        return integrationUser
      } catch (e) {
        console.error(e)
        return null
      }
    },
    [accountId, dispatch, triggerUpdateIntegrationUser],
  )

  const createOrUpdateContact = useCallback(
    async (baseFormValues: ProspectingBaseFormValues, integration: ExternalUserServiceIntegration) => {
      if (contact) {
        return updateContact(contact, integration)
      }
      const createdContact = await createContact(baseFormValues)
      if (!createdContact) {
        return null
      }
      return updateContact(createdContact, integration)
    },
    [contact, createContact, updateContact],
  )

  const getContact = useCallback(
    async (email: string) => {
      try {
        const { data: externalUsersResponse } = await triggerGetAllExternalUsers({ accountId, emails: [email] })
        if (!externalUsersResponse) {
          return null
        }
        const { data: externalUsers } = externalUsersResponse
        if (!externalUsers.length) {
          return null
        }

        const externalUser = externalUsers[0]
        dispatch(setDocumentProspectingContact(externalUser))

        return externalUser
      } catch (e) {
        console.error(e)
        return null
      }
    },
    [accountId, dispatch, triggerGetAllExternalUsers],
  )

  const getIntegrationContact = useCallback(
    async (serviceId: IntegrationServiceId, email: string) => {
      try {
        const { data: integrationUsersResponse } = await triggerGetIntegrationUsers({
          accountId,
          connector: serviceId,
          filters: { email },
        })
        if (!integrationUsersResponse) {
          return null
        }

        const { data: integrationUsers } = integrationUsersResponse
        if (!integrationUsers.length) {
          return null
        }

        const integrationUser = integrationUsers[0]
        dispatch(setDocumentProspectingIntegrationContact(integrationUser))

        return integrationUser
      } catch (e) {
        console.error(e)
        return null
      }
    },
    [accountId, dispatch, triggerGetIntegrationUsers],
  )

  return {
    createContact,
    updateContact,
    getContact,
    createIntegrationContact,
    updateIntegrationContact,
    getIntegrationContact,
    createOrUpdateContact,
  }
}

export const useProspectingDetailsForm = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const selectedKeys = useAppSelector(selectSelectedKeys)
  const serviceId = useAppSelector(selectDocumentProspectingServiceId)
  const integrationContactExternalId = useAppSelector(selectDocumentProspectingIntegrationContactExternalId)
  const baseFormValues = useAppSelector(selectDocumentProspectingBaseFormValues)

  const [triggerExecuteAiAgent] = useExecuteAiAgentMutation()

  const [submitLoading, setSubmitLoading] = useState<boolean>(false)

  const { createOrUpdateContact, createIntegrationContact, updateIntegrationContact } = useDocumentProspectingContacts()

  const executeDetailsFormValueGenerationAiAgent = useCallback(
    async (serviceId: IntegrationServiceId, attachment: AIAgentAttachment, schema: Record<string, unknown>, previousValues: Record<string, unknown>) => {
      try {
        const aiAgentResult = await triggerExecuteAiAgent({
          accountId,
          payload: {
            type: AIAgentType.DOCUMENT_PROSPECTING_DETAILS_FORM_VALUE_GENERATION,
            context: {
              previousValues,
              schema,
            },
            attachments: [attachment],
          },
        }).unwrap()
        if (!aiAgentResult) {
          return null
        }
        return processContactUpdateAgentResult(serviceId, schema, previousValues, aiAgentResult)
      } catch (e) {
        console.error(e)
        return null
      }
    },
    [accountId, triggerExecuteAiAgent],
  )

  const generateSegmentedDetailsFormValues = useCallback(
    async (
      serviceId: IntegrationServiceId,
      attachment: AIAgentAttachment,
      schema: SegmentedDocumentProspectingDetailsFormSchemaType,
      previousValues: SegmentedDocumentProspectingDetailsFormValuesType,
    ) => {
      const keys = Object.keys(schema)

      const detailsFormValues = await Promise.all(
        keys.map((key) => executeDetailsFormValueGenerationAiAgent(serviceId, attachment, schema[key].schema, previousValues[key].values)),
      )

      return Object.fromEntries(keys.map((key, index) => [key, detailsFormValues[index]]))
    },
    [executeDetailsFormValueGenerationAiAgent],
  )

  const generateDetailsFormValuesForIntegration = useCallback(
    async (serviceId: IntegrationServiceId, attachment: AIAgentAttachment, props: DocumentProspectingDetailsFormProps) => {
      if (props.type === 'segmented') {
        return generateSegmentedDetailsFormValues(serviceId, attachment, props.schema, props.previousValues)
      }
      return executeDetailsFormValueGenerationAiAgent(serviceId, attachment, props.schema, props.previousValues)
    },
    [executeDetailsFormValueGenerationAiAgent, generateSegmentedDetailsFormValues],
  )

  const generateFormValues = useCallback(
    async (serviceId: IntegrationServiceId, attachment: AIAgentAttachment, props: DocumentProspectingDetailsFormProps) => {
      dispatch(setDocumentProspectingDetailsFormValuesLoading(true))
      try {
        const detailsFormValues = await generateDetailsFormValuesForIntegration(serviceId, attachment, props)
        dispatch(setDocumentProspectingDetailsFormValues({ type: props.type, data: detailsFormValues }))
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setDocumentProspectingDetailsFormValuesLoading(false))
      }
    },
    [generateDetailsFormValuesForIntegration, dispatch],
  )

  const handleDetailsFormSubmitForIntegration = useCallback(
    async (serviceId: IntegrationServiceId, baseFormValues: ProspectingBaseFormValues, detailsFormValues: ProspectingDetailsFormValues) => {
      if (INTEGRATIONS_WITH_DYNAMIC_SCHEMA.includes(serviceId)) {
        if (!integrationContactExternalId) {
          return null
        }
        return updateIntegrationContact(serviceId, integrationContactExternalId, detailsFormValues)
      }

      const integrationContact = await createIntegrationContact(serviceId, baseFormValues, detailsFormValues)
      if (!integrationContact) {
        return null
      }

      const integration = integrationContact.integrations?.find((integration) => integration.serviceId === serviceId)
      if (!integration) {
        return null
      }

      await createOrUpdateContact(baseFormValues, integration)

      return integrationContact
    },
    [createIntegrationContact, createOrUpdateContact, integrationContactExternalId, updateIntegrationContact],
  )

  const submitFormValues = useCallback(
    async (detailsFormValues: ProspectingDetailsFormValues) => {
      if (!serviceId || !baseFormValues) {
        return false
      }

      const filteredDetailsFormValues = removeEmptyFields(serviceId, filterDataByKeys(detailsFormValues, selectedKeys)) as ProspectingDetailsFormValues
      if (!Object.keys(filteredDetailsFormValues).length) {
        return false
      }

      setSubmitLoading(true)
      try {
        const integrationContact = await handleDetailsFormSubmitForIntegration(serviceId, baseFormValues, filteredDetailsFormValues)
        if (!integrationContact) {
          return false
        }
        dispatch(setDocumentProspectingIntegrationAsSuccess(serviceId))
        return true
      } finally {
        setSubmitLoading(false)
      }
    },
    [baseFormValues, dispatch, handleDetailsFormSubmitForIntegration, selectedKeys, serviceId],
  )

  return { submitLoading, submitFormValues, generateFormValues }
}

export const useProspectingBaseForm = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const serviceId = useAppSelector(selectDocumentProspectingServiceId)

  const [triggerExecuteAiAgent] = useExecuteAiAgentMutation()

  const [submitLoading, setSubmitLoading] = useState<boolean>(false)

  const { getContact, getIntegrationContact, createContact, createIntegrationContact, createOrUpdateContact } = useDocumentProspectingContacts()

  const generateFormValues = useCallback(
    async (attachment: AIAgentAttachment) => {
      dispatch(setDocumentProspectingBaseFormValuesLoading(true))
      dispatch(setDocumentProspectingAttachment(attachment))
      try {
        const { data: aiAgentResult } = await triggerExecuteAiAgent({
          accountId,
          payload: {
            type: AIAgentType.DOCUMENT_PROSPECTING_BASE_FORM_VALUE_GENERATION,
            context: {},
            attachments: [attachment],
          },
        })
        if (!aiAgentResult) {
          return null
        }
        const { result } = aiAgentResult as DocumentProspectingBaseFormExecutionResultType
        const baseFormValues = mapProspectingBaseFormExecutionResult(result)
        dispatch(setDocumentProspectingBaseFormValues(baseFormValues))
        return baseFormValues
      } catch (e) {
        console.error(e)
        return null
      } finally {
        dispatch(setDocumentProspectingBaseFormValuesLoading(false))
      }
    },
    [accountId, dispatch, triggerExecuteAiAgent],
  )

  const submitFormValues = useCallback(
    async (baseFormValues: ProspectingBaseFormValues) => {
      dispatch(setDocumentProspectingBaseFormValues(baseFormValues))

      setSubmitLoading(true)
      try {
        const { email } = baseFormValues
        const existingContact = await getContact(email)

        if (!serviceId) {
          if (existingContact) {
            return false
          }
          const createdContact = await createContact(baseFormValues)
          return !!createdContact
        }

        const existingIntegrationContact = await getIntegrationContact(serviceId, email)
        if (existingIntegrationContact) {
          return false
        }

        if (!INTEGRATIONS_WITH_DYNAMIC_SCHEMA.includes(serviceId)) {
          return true
        }

        const integrationContact = await createIntegrationContact(serviceId, baseFormValues)
        if (!integrationContact) {
          return false
        }

        const integration = integrationContact.integrations?.find((integration) => integration.serviceId === serviceId)
        if (!integration) {
          return false
        }

        await createOrUpdateContact(baseFormValues, integration)

        const externalInfo = integrationContact.externalInfos.find((integration) => integration.serviceId === serviceId)
        if (!externalInfo) {
          return false
        }

        const { contactSchema: schema, contactValues: previousValues } = externalInfo
        const propsType = INTEGRATIONS_WITH_SEGMENTED_CONTACT_CHANGE.includes(serviceId) ? 'segmented' : 'default'
        dispatch(setDocumentProspectingDetailsFormProps({ type: propsType, schema, previousValues }))

        return true
      } finally {
        setSubmitLoading(false)
      }
    },
    [createContact, createIntegrationContact, createOrUpdateContact, dispatch, getContact, getIntegrationContact, serviceId],
  )

  return { submitLoading, submitFormValues, generateFormValues }
}

export const useShowSummaryForDocumentProspecting = () => {
  const accountId = useAppSelector(selectAccountId)
  const documentId = useAppSelector(selectDocumentProspectingDocumentId)
  const contact = useAppSelector(selectDocumentProspectingContact)

  const [triggerProcessDocuments, { isLoading: isProcessing }] = useProcessDocumentsMutation()

  const { tracked } = useTracking()

  const navigate = useNavigate()

  const _showSummary = useCallback(async () => {
    if (!contact || !documentId || isProcessing) {
      return
    }

    try {
      const documents = await triggerProcessDocuments({ accountId, payload: { documentIds: [documentId], contactId: contact.id } }).unwrap()
      if (!documents.length) {
        return
      }

      navigate(`/documents/${documents[0].id}`)
    } catch (e) {
      console.error(e)
      Sentry.captureException(e)
    }
  }, [accountId, contact, documentId, isProcessing, navigate, triggerProcessDocuments])

  const showSummary = useMemo(
    () =>
      tracked(_showSummary, {
        eventName: TrackEvent.PROCESS_DOCUMENTS_CLICKED,
        eventDetails: { documentCount: 1, source: 'manual_upload', context: 'new_contact' },
      }),
    [_showSummary, tracked],
  )

  return { showSummary, isProcessing }
}

export const useDocumentProspectingFileUpload = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)

  const [triggerCreateDocument] = useCreateDocumentMutation()
  const [triggerUploadFile] = useUploadFileMutation()
  const [markDocumentFileUploaded] = useMarkDocumentFileUploadedMutation()

  const uploadFile = async (file: File): Promise<string | null> => {
    dispatch(setDocumentProspectingSelectedFileName(file.name))
    dispatch(setDocumentProspectingUploadIsLoading(true))
    try {
      const hash = await hashFile(file)

      const payload: DocumentCreate = {
        name: file.name,
        files: [{ type: DocumentFileType.ZOCKS, name: file.name, size: file.size, contentType: file.type, hash }],
      }
      const { document, fileUploadTargets } = await triggerCreateDocument({ accountId, payload }).unwrap()

      const [uploadTarget] = fileUploadTargets
      if (!uploadTarget) {
        return null
      }

      await triggerUploadFile({ uploadUrl: uploadTarget.fileUploadUrl, uploadParameters: uploadTarget.fileUploadParameters, file }).unwrap()

      await markDocumentFileUploaded({ accountId, documentId: uploadTarget.documentId, fileId: uploadTarget.fileId }).unwrap()

      dispatch(setDocumentProspectingDocumentId(document.id))
      dispatch(setDocumentProspectingUploadedFileId(uploadTarget.fileId))

      return uploadTarget.fileId
    } catch (e) {
      console.error(e)
      return null
    } finally {
      dispatch(setDocumentProspectingUploadIsLoading(false))
    }
  }

  const selectIntegrationFile = async (serviceId: IntegrationServiceId, item: FileBrowserItem): Promise<boolean> => {
    dispatch(setDocumentProspectingSelectedFileName(item.name))
    try {
      const payload: DocumentCreate = {
        name: item.name,
        files: [
          {
            type: DocumentFileType.INTEGRATION,
            fileId: item.id,
            connector: serviceId,
            name: item.name,
            size: item.size ?? 0,
            contentType: inferContentType(item.name),
          },
        ],
      }
      const { document } = await triggerCreateDocument({ accountId, payload }).unwrap()

      dispatch(setDocumentProspectingDocumentId(document.id))

      return true
    } catch (e) {
      console.error(e)
      return false
    }
  }

  return { uploadFile, selectIntegrationFile }
}
